import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import importDataService from 'features/ImportData/importDataService';
import { useEffect } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { STATUS_SUCCESS, displayError, handleServiceError } from 'utils';
import * as yup from 'yup';
import { CounterPattern } from '../../models';

export interface ChangeRegistrationPatternNameModalProps {
  onClose?: () => void;
}
type FormValues = {
  counterPatterns: CounterPattern[];
};
function ChangeRegistrationPatternNameModal({ onClose }: ChangeRegistrationPatternNameModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  // selector
  const isEditMode = useAppSelector(selectIsEditMode);

  // yup setup
  const schema = yup.object({
    counterPatterns: yup
      .array(
        yup.object({
          id: yup.number(),
          cd: yup.string().required(t('SaleTypeDetail.warn_field_required')),
          name: yup.string().required(t('SaleTypeDetail.warn_field_required')),
        })
      )
      .test('unique-cd', 'パターン番号が重複しているため、登録できません。', (list) => {
        if (!list) return true;

        const hasEmptyCd = list.some((item) => {
          if (item.cd === '' || item.name.length === 0) return true;

          return false;
        });
        if (hasEmptyCd) return true;

        const cds = list.map((item) => item.cd);
        const uniqueCds = new Set(cds);

        return uniqueCds.size === cds.length;
      }),
  });

  // form setup
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      counterPatterns: [],
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { fields, append, remove } = useFieldArray({
    name: 'counterPatterns',
    control,
    keyName: 'key',
  });

  function loadData() {
    (async () => {
      // TODO: add try/catch
      const response = await importDataService.get();
      if (response.status === STATUS_SUCCESS) {
        reset({ counterPatterns: response.data });
      }
    })();
  }

  const handleClose = () => {
    onClose && onClose();
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const response = await importDataService.update(data.counterPatterns);
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} onClose={handleClose} />);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  };

  function handleAdd() {
    append({ id: -1, cd: '', name: '', counter_pattern_version: null });
  }

  const onInvalid = (errors: any) => {
    const err = errors?.counterPatterns;
    const type = err?.type || err?.root?.type;

    if (type === 'unique-cd') {
      showGlobalModal(() => <AlertModal title={t('App.warning')} message={err?.message || err?.root?.message} />);
    }
  };

  return (
    <Modal show backdrop="static" onHide={handleClose} centered>
      <Modal.Header className={isEditMode ? 'reedit-mode-wrapper ' : ''} closeButton></Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper ' : ''}>
        <div className="mb-3">
          <FeatureTitleBar title={t('ChangeRegistrationPatternNameModal.head_title')} />
        </div>
        <Form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div className="d-flex justify-content-between">
            <Button type="button" className="btn--dark-green" onClick={handleAdd}>
              新規追加
            </Button>
            <Button type="submit" className="btn--dark-green">
              {t('SaleTypeDetail.save')}
            </Button>
          </div>
          <div className="sticky-table flex-grow-1 flex-shrink-1 mt-2" style={{ maxHeight: '311px' }}>
            <Table hover>
              <thead>
                <tr>
                  <th style={{ width: '100px' }}>
                    {t('ChangeRegistrationPatternNameModal.Table.Header.pattern_number')}
                  </th>
                  <th>{t('ChangeRegistrationPatternNameModal.Table.Header.pattern_name')}</th>
                  <th>{t('ChangeRegistrationPatternNameModal.Table.Header.version')}</th>
                  <th>アクション</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => {
                  return (
                    <tr key={`${index}-${field.id}`}>
                      <td className="text-center">
                        {field.id === -1 ? (
                          <>
                            <Form.Control
                              className="text-center"
                              maxLength={10}
                              {...register(`counterPatterns.${index}.cd`)}
                            ></Form.Control>
                            {errors?.counterPatterns?.[index]?.cd && (
                              <span className="small text-danger d-inline-block mt-1">
                                {errors?.counterPatterns?.[index]?.cd?.message}
                              </span>
                            )}
                          </>
                        ) : (
                          field.cd
                        )}
                      </td>
                      <td>
                        <Form.Control
                          className="text-center"
                          {...register(`counterPatterns.${index}.name`)}
                          maxLength={255}
                          defaultValue={field.name}
                        ></Form.Control>
                        {errors?.counterPatterns?.[index]?.name && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.counterPatterns?.[index]?.name?.message}
                          </span>
                        )}
                      </td>
                      <td className="text-center">{field.counter_pattern_version ?? '未設定'}</td>
                      <td>
                        {field.id === -1 && (
                          <Button type="button" className="btn--red" onClick={() => remove(index)}>
                            削除
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
export default ChangeRegistrationPatternNameModal;
